import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spinner, Container } from 'react-bootstrap';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireEmailVerification?: boolean;
}

// Paths that should remain accessible even when trial is expired (billing + account management)
const TRIAL_ALLOWED_PATHS = ['/select-plan', '/plan-confirmation', '/dashboard', '/help'];

// Paths that should remain accessible before onboarding is completed (billing flows only)
const ONBOARDING_ALLOWED_PATHS = ['/select-plan', '/plan-confirmation'];

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requireEmailVerification = false
}) => {
  const { currentUser, loading, trialStatus, userProfile } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (!currentUser) {
    return <Navigate to="/signin" />;
  }

  if (requireEmailVerification && !currentUser.emailVerified) {
    return <Navigate to="/verify-email" />;
  }

  // Check trial expiration - redirect to select-plan if expired
  // But don't block access to billing-related and account management pages
  const isTrialAllowedPath = TRIAL_ALLOWED_PATHS.some(path => location.pathname.startsWith(path));
  if (trialStatus?.isTrialExpired && !isTrialAllowedPath) {
    return <Navigate to="/select-plan" state={{ trialExpired: true, from: location }} replace />;
  }

  // Check onboarding completion - redirect to onboarding if not completed
  // Only billing pages are exempt; dashboard/help require onboarding first
  const hasCompletedOnboarding = !!userProfile?.onboardingCompleted;
  const isOnboardingAllowedPath = ONBOARDING_ALLOWED_PATHS.some(path => location.pathname.startsWith(path));
  if (
    userProfile &&
    !hasCompletedOnboarding &&
    !isOnboardingAllowedPath &&
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;